import { motion } from "framer-motion";
import { ArrowUpRight, Bot, ChevronDown, Globe, MessageCircle, Target, Workflow } from "lucide-react";
import { MaskedLine } from "../components/Reveal";
import { WhatsAppIcon } from "../components/icons";
import { SERVICES, WHATSAPP_URL, waLink } from "../data/site";

const ICONS = { MessageCircle, Globe, Bot, Workflow, Target };

export const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
    <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(120%_90%_at_15%_0%,rgba(0,60,160,0.28),transparent_55%)]" />
    <div className="pointer-events-none absolute inset-0 z-[2] bg-[#05070D]/60 md:hidden" />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-48 bg-gradient-to-t from-[#05070D] to-transparent" />

    <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-6 pb-28 pt-36 lg:px-10 xl:max-w-none xl:grid-cols-[1fr_auto] xl:px-[5vw]">
      <div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.9 }}
        className="mb-8 text-[11px] font-medium uppercase tracking-[0.4em] text-slate-400"
        data-testid="hero-eyebrow"
      >
        IA <span className="text-[#3a9bff]">•</span> Automações <span className="text-[#3a9bff]">•</span> Marketing
      </motion.p>

      <h1 className="max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-7xl" data-testid="hero-headline">
        <MaskedLine delay={0.15}>Ideias estratégicas.</MaskedLine>
        <MaskedLine delay={0.32}>Tecnologia que gera</MaskedLine>
        <MaskedLine delay={0.49} className="text-[#3a9bff]">resultados.</MaskedLine>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
        data-testid="hero-subtext"
      >
        Sites, automações e inteligência artificial sob medida para pequenos negócios
        que querem crescer com estrutura — e não no improviso.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-whatsapp-cta"
          className="group inline-flex items-center gap-3 rounded-full bg-[#007BFF] px-8 py-4 text-sm font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_42px_rgba(0,123,255,0.6)]"
        >
          <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
          Falar no WhatsApp
        </a>
        <a
          href="#solucoes"
          data-testid="hero-solutions-link"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-[#007BFF]/60 hover:text-white"
        >
          Ver soluções
        </a>
      </motion.div>
      </div>

      {/* telas largas: à direita, com o monograma 3D no meio (Scene3D desloca ele quando >= 1280px); menores: abaixo dos botões */}
      <motion.nav
        aria-label="Áreas de atuação"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0B0F14]/70 p-4 backdrop-blur-md sm:p-6 xl:w-[360px]"
        data-testid="hero-areas"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Áreas de atuação</p>
        <ul className="mt-4 space-y-1">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] ?? Globe;
            return (
              <li key={s.name}>
                <a
                  href={waLink(s.cta)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-slate-200 transition-colors duration-300 hover:bg-[#007BFF]/10 hover:text-white"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#007BFF]/25 bg-[#007BFF]/10 text-[#3a9bff]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1">{s.name}</span>
                  <ArrowUpRight className="h-4 w-4 text-slate-500 transition-[transform,color] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#3a9bff]" />
                </a>
              </li>
            );
          })}
        </ul>
      </motion.nav>
    </div>

    <motion.a
      href="#solucoes"
      aria-label="Rolar para soluções"
      data-testid="hero-scroll-indicator"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.6, duration: 1 }}
      className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500"
    >
      <ChevronDown className="h-6 w-6 animate-bounce" />
    </motion.a>
  </section>
);
